import React, { useEffect, useRef, useState } from "react";
import {
  submitStockChatQuery,
  StockChatResponse,
} from "../services/stockChatbot.service";
import "../styles/stockChatbot.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ApexChart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";

type ChartSeries = {
  name: string;
  values: Array<number | null>;
  kind?: "actual" | "forecast";
};


type ChartData = {
  type: "line" | "bar";
  metric: string;
  labels: string[];
  series: ChartSeries[];
  yMin?: number | null;
  yMax?: number | null;
  latest?: number | null;
  previous?: number | null;
  unit?: string | null;
  splitAdjusted?: boolean;
};

type ChatMessage = {
  id: string;
  role: "user" | "bot";
  text: string;
  cached?: boolean;
  chart?: ChartData | null;
  charts?: ChartData[] | null;
  plan?: any;
  meta?: StockChatResponse["meta"];
};

const examples: string[] = [];

const createId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;

const formatMetricLabel = (raw?: string | null) => {
  if (!raw) return "Metric";
  const cleaned = String(raw)
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "Metric";
  const titled = cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
  const replacements: Record<string, string> = {
    Eps: "EPS",
    Dps: "DPS",
    Ebitda: "EBITDA",
    Fcf: "FCF",
    Roe: "ROE",
    Roa: "ROA",
    Pe: "P/E",
    Pbt: "PBT",
    Ccc: "CCC",
  };
  return titled
    .split(" ")
    .map((word) => replacements[word] || word)
    .join(" ");
};

const normalizeAnswer = (answer: string) => {
  let text = answer ?? "";
  if (!text) return text;
  const patterns = [
    /No future sales estimates[^.]*\.\s*/gi,
    /No future (?:sales )?estimates[^.]*\.\s*/gi,
    /No quarterly breakdowns[^.]*\.\s*/gi,
    /No future .*?breakdowns[^.]*\.\s*/gi,
    /To estimate next year'?s sales[^.]*\.\s*/gi,
    /To estimate future sales[^.]*\.\s*/gi,
  ];
  let removed = false;
  patterns.forEach((pattern) => {
    if (pattern.test(text)) {
      removed = true;
      text = text.replace(pattern, "");
    }
  });
  text = text.replace(/\n{3,}/g, "\n\n").trim();
  if (removed && !/Forecast data is not available/i.test(text)) {
    const notice =
      "Forecast data is not available. Please check for future updates or consult reports for projections.";
    text = text ? `${text}\n\n${notice}` : notice;
  }
  return text;
};

const StockChatbot: React.FC = (): React.ReactElement => {
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: createId(),
      role: "bot",
      text: "Welcome. Ask about prices, fundamentals, forecasts, or peer comparisons.",
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const chatWindowRef = useRef<HTMLDivElement | null>(null);

  const sendQuestion = async (value?: string) => {
    const content = (value ?? question).trim();
    if (!content || loading) return;

    setQuestion("");
    setError(null);

    const userMsg: ChatMessage = { id: createId(), role: "user", text: content };

    setMessages((prev) => [...prev, userMsg]);
    setLoading(true);

    try {
      const data: StockChatResponse = await submitStockChatQuery({
        question: content,
      });

      if (!data || !data.answer) {
        throw new Error("Query failed");
      }

      const botMsg: ChatMessage = {
        id: createId(),
        role: "bot",
        text: normalizeAnswer(data.answer),
        plan: data.plan,
        chart: data.chart as ChartData,
        charts: (data as StockChatResponse).charts as ChartData[],
        cached: data.cached,
        meta: data.meta,
      };
      setMessages((prev) => [...prev, botMsg]);
    } catch (err: any) {
      const message =
        err?.response?.data?.error ||
        err?.message ||
        "Unable to run the query right now.";
      setMessages((prev) => [
        ...prev,
        { id: createId(), role: "bot", text: `Error: ${message}` },
      ]);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const el = chatWindowRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  return (
    <div className="stock-chat px-4 py-3">
      <div className="stock-chat-header">
        <div>
          <p className="stock-chat-eyebrow">Enterprise Stock Analytics</p>
          <h2 className="stock-chat-title">Institutional-quality insights with chart-backed evidence.</h2>
          <p className="stock-chat-lede">
            Submit a question and receive organized outputs with key metrics, clear context,
            and visual support for faster decision-making.
          </p>
        </div>
        {examples.length > 0 && (
          <div className="stock-chat-examples">
            {examples.map((ex) => (
              <button
                key={ex}
                className="stock-chat-pill-btn"
                onClick={() => sendQuestion(ex)}
                disabled={loading}
                type="button"
              >
                {ex}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="stock-chat-shell">
        <div className="stock-chat-window" ref={chatWindowRef}>
          {messages.map((msg) => (
            <Message key={msg.id} msg={msg} onQuickAction={sendQuestion} />
          ))}
          {loading && <div className="stock-chat-typing">Processing your request...</div>}
        </div>

        <form
          className="stock-chat-composer"
          onSubmit={(e) => {
            e.preventDefault();
            sendQuestion();
          }}
        >
          <input
            placeholder="e.g. Provide ACL.N free cash flow for the last 5 quarters"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            disabled={loading}
          />
          <button type="submit" disabled={loading}>
            {loading ? "Running..." : "Ask"}
          </button>
        </form>
        {error && <div className="stock-chat-error">Error: {error}</div>}
      </div>
    </div>
  );
};

const Message: React.FC<{
  msg: ChatMessage;
  onQuickAction: (value: string) => void;
}> = ({ msg, onQuickAction }) => {
  return (
    <div
      className={`stock-chat-bubble ${
        msg.role === "user" ? "user" : "bot"
      }`}
    >
      <div className="stock-chat-bubble-head">
        <span className="stock-chat-pill">
          {msg.role === "user" ? "You" : "Assistant"}
        </span>
        {msg.cached && <span className="stock-chat-pill green">cached</span>}
      </div>
      <div className="stock-chat-bubble-text stock-chat-markdown">
        {msg.role === "bot" ? (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
          >
            {msg.text}
          </ReactMarkdown>
        ) : (
          msg.text
        )}
      </div>
      {msg.meta?.lastUpdated && (
        <div className="stock-chat-meta">
          <span className={`stock-chat-pill ${msg.meta.stale ? "red" : ""}`}>
            Last updated: {msg.meta.lastUpdated}
            {msg.meta.stale ? " (stale)" : ""}
          </span>
        </div>
      )}
      {null}
      {(msg.chart || (msg.charts && msg.charts.length)) && (
        <Chart chart={msg.chart || undefined} charts={msg.charts || undefined} />
      )}
    </div>
  );
};

const Chart: React.FC<{ chart?: ChartData | null; charts?: ChartData[] | null }> = ({
  chart,
  charts,
}) => {
  const chartList = (charts && charts.length ? charts : chart ? [chart] : []).filter(
    Boolean
  ) as ChartData[];
  const [selectedIdx, setSelectedIdx] = useState(0);

  useEffect(() => {
    setSelectedIdx(0);
  }, [charts?.length, chart?.metric]);

  if (!chartList.length) {
    return <div className="stock-chat-nochart">No chart available.</div>;
  }

  const activeChart = chartList[Math.min(selectedIdx, chartList.length - 1)];
  const unit = activeChart.unit || null;
  const formatAbsoluteNumber = (value: number) => {
    const abs = Math.abs(value);
    return value.toLocaleString(undefined, {
      maximumFractionDigits: abs >= 1000 ? 0 : 2,
      minimumFractionDigits: 0,
    });
  };
  const formatValue = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value)))
      return "N/A";
    const num = Number(value);
    if (unit === "%") return `${num.toFixed(2)}%`;
    if (unit === "days") return `${num.toFixed(0)} days`;
    if (unit === "x") return `${num.toFixed(2)}x`;
    return formatAbsoluteNumber(num);
  };
  const formatDelta = (value: number | null | undefined) => {
    if (value === null || value === undefined || Number.isNaN(Number(value)))
      return "N/A";
    const num = Number(value);
    const sign = num >= 0 ? "+" : "";
    if (unit === "%") return `${sign}${num.toFixed(2)}%`;
    if (unit === "days") return `${sign}${num.toFixed(0)} days`;
    if (unit === "x") return `${sign}${num.toFixed(2)}x`;
    return `${sign}${formatAbsoluteNumber(num)}`;
  };
  const formatPct = (value: number | null | undefined) =>
    value === null || value === undefined || Number.isNaN(Number(value))
      ? "N/A"
      : `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
  const formatAxisValue = (value: number) => {
    if (!Number.isFinite(value)) return "N/A";
    if (unit === "%") return `${Number(value).toFixed(0)}%`;
    if (unit === "days") return `${Number(value).toFixed(0)}d`;
    if (unit === "x") return `${Number(value).toFixed(1)}x`;
    return Number(value).toLocaleString();
  };
  const toNumber = (value: number | null | undefined) =>
    value === null || value === undefined || Number.isNaN(Number(value))
      ? null
      : Number(value);

  const labels = Array.isArray(activeChart.labels) ? activeChart.labels : [];
  const seriesList = Array.isArray(activeChart.series) ? activeChart.series : [];
  const actualSeriesList = seriesList.filter((series) => series.kind !== "forecast");
  const primarySeries = actualSeriesList[0] || seriesList[0] || { values: [] };
  const seriesValues = seriesList.flatMap((s) =>
    (Array.isArray(s.values) ? s.values : []).filter(
      (v) => v !== null && Number.isFinite(v as number)
    ) as number[]
  );
  const fallbackMax = seriesValues.length ? Math.max(...seriesValues) : 1;
  const fallbackMin = seriesValues.length ? Math.min(...seriesValues) : 0;
  const yMax =
    activeChart.yMax !== undefined && activeChart.yMax !== null
      ? activeChart.yMax
      : fallbackMax || 1;
  const yMin =
    activeChart.yMin !== undefined && activeChart.yMin !== null
      ? activeChart.yMin
      : fallbackMin;
  const values = Array.isArray(primarySeries.values) ? primarySeries.values : [];
  const numericPoints = values
    .map((value, index) => ({
      value: toNumber(value),
      index,
    }))
    .filter((point) => point.value !== null) as Array<{
    value: number;
    index: number;
  }>;
  const latestPoint = numericPoints.length
    ? numericPoints[numericPoints.length - 1]
    : null;
  const previousPoint =
    numericPoints.length > 1 ? numericPoints[numericPoints.length - 2] : null;
  const latest = activeChart.latest ?? latestPoint?.value ?? null;
  const previous = activeChart.previous ?? previousPoint?.value ?? null;
  const delta =
    latest !== null && latest !== undefined && previous !== null && previous !== undefined
      ? latest - previous
      : null;
  const deltaPct =
    delta !== null && previous !== null && previous !== 0
      ? (delta / Math.abs(previous)) * 100
      : null;
  const deltaPctValue = Number.isFinite(deltaPct ?? NaN)
    ? Number(deltaPct)
    : null;
  const avgValue = numericPoints.length
    ? numericPoints.reduce((sum, point) => sum + point.value, 0) /
      numericPoints.length
    : null;
  const minPoint = numericPoints.reduce<{
    value: number;
    index: number;
  } | null>((acc, point) => {
    if (!acc || point.value < acc.value) return point;
    return acc;
  }, null);
  const maxPoint = numericPoints.reduce<{
    value: number;
    index: number;
  } | null>((acc, point) => {
    if (!acc || point.value > acc.value) return point;
    return acc;
  }, null);
  const rangeLabel = labels.length
    ? `${labels[0]} -> ${labels[labels.length - 1]}`
    : "Range: N/A";
  const actualPalette = ["#1f9d8a", "#3f7ad6", "#e76a5b", "#6c8b47", "#8a5cf6"];
  const forecastColor = "#f1a23b";
  let actualColorIdx = 0;
  const seriesColors = seriesList.map((series) => {
    if (series.kind === "forecast") return forecastColor;
    const color = actualPalette[actualColorIdx % actualPalette.length];
    actualColorIdx += 1;
    return color;
  });
  const hasForecastOverlay = seriesList.some((series) => series.kind === "forecast");
  const isMultiSeries = actualSeriesList.length > 1;
  const seriesStats = seriesList
    .map((series, idx) => {
    const points = (Array.isArray(series.values) ? series.values : [])
      .map((value, index) => ({
        value: toNumber(value),
        index,
      }))
      .filter((point) => point.value !== null) as Array<{
      value: number;
      index: number;
    }>;
    const latestPoint = points.length ? points[points.length - 1] : null;
    const previousPoint = points.length > 1 ? points[points.length - 2] : null;
    const latestValue = latestPoint?.value ?? null;
    const previousValue = previousPoint?.value ?? null;
    const deltaValue =
      latestValue !== null && previousValue !== null
        ? latestValue - previousValue
        : null;
    const deltaPctValue =
      deltaValue !== null && previousValue !== null && previousValue !== 0
        ? (deltaValue / Math.abs(previousValue)) * 100
        : null;
    return {
      name: series.name || `Series ${idx + 1}`,
      color: seriesColors[idx] || actualPalette[0],
      kind: series.kind || "actual",
      latestValue,
      deltaValue,
      deltaPctValue,
      label: latestPoint ? labels[latestPoint.index] : null,
    };
    })
    .filter((series) => series.kind !== "forecast");

  const chartType = activeChart.type === "bar" ? "bar" : "line";
  const apexSeries = seriesList.map((series, idx) => ({
    name:
      series.name ||
      (seriesList.length > 1 ? `Series ${idx + 1}` : activeChart.metric),
    data: (Array.isArray(series.values) ? series.values : []).map(toNumber),
  }));
  const hasChartData =
    apexSeries.length > 0 &&
    apexSeries.some((series) =>
      series.data.some(
        (value) => typeof value === "number" && Number.isFinite(value)
      )
    );
  const options: ApexOptions = {
    chart: {
      type: chartType,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, easing: "easeinout", speed: 500 },
      foreColor: "#556073",
      fontFamily: "Space Grotesk, Segoe UI, sans-serif",
    },
    colors: seriesColors,
    dataLabels: { enabled: false },
    stroke:
      chartType === "line"
        ? {
            curve: "smooth",
            width: seriesList.map(() => 3),
            dashArray: seriesList.map((series) =>
              series.kind === "forecast" ? 7 : 0
            ),
          }
        : { width: 0 },
    markers:
      chartType === "line"
        ? { size: 3, strokeWidth: 2, strokeColors: "#ffffff" }
        : { size: 0 },
    grid: {
      borderColor: "#e5ddd3",
      strokeDashArray: 4,
      padding: { left: 8, right: 12, top: 8, bottom: 0 },
    },
    xaxis: {
      categories: labels,
      labels: {
        rotate: -30,
        trim: true,
        style: { colors: "#6a7486", fontSize: "11px" },
      },
    },
    yaxis: {
      min: activeChart.yMin ?? undefined,
      max: activeChart.yMax ?? undefined,
      labels: { formatter: (value) => formatAxisValue(value as number) },
    },
    tooltip: {
      y: { formatter: (value) => formatValue(value as number) },
    },
    legend: {
      show: chartType === "line" && (isMultiSeries || hasForecastOverlay),
      position: "top",
      horizontalAlign: "right",
    },
    fill:
      chartType === "line"
        ? {
            type: "gradient",
            gradient: {
              shadeIntensity: 0.35,
              opacityFrom: 0.45,
              opacityTo: 0.05,
              stops: [0, 90, 100],
            },
          }
        : { opacity: 0.9 },
  };

  if (chartType === "bar") {
    options.plotOptions = {
      bar: {
        columnWidth: "55%",
        borderRadius: 6,
        distributed: seriesList.length === 1,
      },
    };
  }

  if (activeChart.type === "bar") {
    const barPoints = labels.map((label, idx) => ({
      label,
      value: toNumber(
        (Array.isArray(seriesList[0]?.values) ? seriesList[0]?.values : [])[
          idx
        ]
      ),
    }));
    const numericBars = barPoints.filter(
      (point) => point.value !== null && Number.isFinite(point.value)
    ) as Array<{ label: string; value: number }>;
    const topBar = numericBars.reduce<{ label: string; value: number } | null>(
      (acc, point) => {
        if (!acc || point.value > acc.value) return point;
        return acc;
      },
      null
    );
    const bottomBar = numericBars.reduce<{ label: string; value: number } | null>(
      (acc, point) => {
        if (!acc || point.value < acc.value) return point;
        return acc;
      },
      null
    );
    const spread =
      topBar && bottomBar ? topBar.value - bottomBar.value : null;

    return (
      <div className="stock-chat-card">
        <div className="stock-chat-chart-head">
          <div>
            <p className="stock-chat-muted">Chart</p>
            <strong>{formatMetricLabel(activeChart.metric)}</strong>
          </div>
          <div className="stock-chat-pill-row">
            {chartList.length > 1 && (
              <select
                className="stock-chat-metric-select"
                value={selectedIdx}
                onChange={(event) => setSelectedIdx(Number(event.target.value))}
              >
                {chartList.map((item, idx) => (
                  <option key={`${item.metric}-${idx}`} value={idx}>
                    {formatMetricLabel(item.metric)}
                  </option>
                ))}
              </select>
            )}
            <span className="stock-chat-pill">bar</span>
          </div>
        </div>
        {chartList.length > 1 ? (
          <div className="stock-chat-muted small">
            Select a metric from the dropdown to update the chart.
          </div>
        ) : null}
        <div className="stock-chat-muted">{rangeLabel}</div>
      {activeChart.splitAdjusted ? (
        <div className="stock-chat-split-note">Note: Values have been adjusted for stock splits</div>
      ) : null}
        <div className="stock-chat-metrics">
          <div className="stock-chat-metric-card">
            <span className="stock-chat-metric-label">Top</span>
            <span className="stock-chat-metric-value">
              {formatValue(topBar?.value ?? null)}
            </span>
            <span className="stock-chat-metric-foot">
              {topBar?.label || "n/a"}
            </span>
          </div>
          <div className="stock-chat-metric-card">
            <span className="stock-chat-metric-label">Bottom</span>
            <span className="stock-chat-metric-value">
              {formatValue(bottomBar?.value ?? null)}
            </span>
            <span className="stock-chat-metric-foot">
              {bottomBar?.label || "n/a"}
            </span>
          </div>
          <div className="stock-chat-metric-card">
            <span className="stock-chat-metric-label">Spread</span>
            <span className="stock-chat-metric-value">
              {formatValue(spread)}
            </span>
            <span className="stock-chat-metric-foot">Top - Bottom</span>
          </div>
          <div className="stock-chat-metric-card">
            <span className="stock-chat-metric-label">Bars</span>
            <span className="stock-chat-metric-value">
              {labels.length}
            </span>
            <span className="stock-chat-metric-foot">Count</span>
          </div>
        </div>
        <div className="stock-chat-line-wrap stock-chat-apex">
          {hasChartData ? (
            <ApexChart
              options={options}
              series={apexSeries}
              type="bar"
              height={240}
            />
          ) : (
            <div className="stock-chat-nochart">
              No numeric data returned for this chart.
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="stock-chat-card">
      <div className="stock-chat-chart-head">
        <div>
          <p className="stock-chat-muted">Chart</p>
          <strong>{formatMetricLabel(activeChart.metric)}</strong>
        </div>
        <div className="stock-chat-pill-row">
          {chartList.length > 1 && (
            <select
              className="stock-chat-metric-select"
              value={selectedIdx}
              onChange={(event) => setSelectedIdx(Number(event.target.value))}
            >
              {chartList.map((item, idx) => (
                <option key={`${item.metric}-${idx}`} value={idx}>
                  {formatMetricLabel(item.metric)}
                </option>
              ))}
            </select>
          )}
          <span className="stock-chat-pill">line</span>
        </div>
      </div>
      {chartList.length > 1 ? (
        <div className="stock-chat-muted small">
          Select a metric from the dropdown to update the chart.
        </div>
      ) : null}
      <div className="stock-chat-muted">{rangeLabel}</div>
      {hasForecastOverlay ? (
        <div className="stock-chat-muted small">
          Forecast is shown as an orange dashed line.
        </div>
      ) : null}
      {activeChart.splitAdjusted ? (
        <div className="stock-chat-split-note">Note: Values have been adjusted for stock splits</div>
      ) : null}
      {isMultiSeries ? (
        <div className="stock-chat-metrics">
          {seriesStats.map((stat) => (
            <div
              className={`stock-chat-metric-card ${
                stat.deltaValue === null
                  ? ""
                  : stat.deltaValue >= 0
                  ? "positive"
                  : "negative"
              }`}
              key={stat.name}
            >
              <span
                className="stock-chat-metric-label"
                style={{ color: stat.color }}
              >
                {stat.name}
              </span>
              <span className="stock-chat-metric-value">
                {formatValue(stat.latestValue)}
              </span>
              <span className="stock-chat-metric-foot">
                {stat.label || "n/a"}
                {stat.deltaPctValue !== null
                  ? ` ${formatPct(stat.deltaPctValue)}`
                  : ""}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="stock-chat-metrics">
          <div className="stock-chat-metric-card">
            <span className="stock-chat-metric-label">Latest</span>
            <span className="stock-chat-metric-value">
              {formatValue(latest)}
            </span>
            <span className="stock-chat-metric-foot">
              {latestPoint ? labels[latestPoint.index] : "n/a"}
            </span>
          </div>
          <div
            className={`stock-chat-metric-card ${
              delta === null ? "" : delta >= 0 ? "positive" : "negative"
            }`}
          >
            <span className="stock-chat-metric-label">Change</span>
            <span className="stock-chat-metric-value">
              {formatDelta(delta)}
            </span>
            <span className="stock-chat-metric-foot">
              {formatPct(deltaPctValue)}
            </span>
          </div>
          <div className="stock-chat-metric-card">
            <span className="stock-chat-metric-label">High</span>
            <span className="stock-chat-metric-value">
              {formatValue(maxPoint?.value ?? null)}
            </span>
            <span className="stock-chat-metric-foot">
              {maxPoint ? labels[maxPoint.index] : "n/a"}
            </span>
          </div>
          <div className="stock-chat-metric-card">
            <span className="stock-chat-metric-label">Low</span>
            <span className="stock-chat-metric-value">
              {formatValue(minPoint?.value ?? null)}
            </span>
            <span className="stock-chat-metric-foot">
              {minPoint ? labels[minPoint.index] : "n/a"}
            </span>
          </div>
          <div className="stock-chat-metric-card">
            <span className="stock-chat-metric-label">Avg</span>
            <span className="stock-chat-metric-value">
              {formatValue(avgValue)}
            </span>
            <span className="stock-chat-metric-foot">Mean</span>
          </div>
        </div>
      )}
      <div className="stock-chat-line-wrap stock-chat-apex">
        {hasChartData ? (
          <ApexChart
            options={options}
            series={apexSeries}
            type="line"
            height={240}
          />
        ) : (
          <div className="stock-chat-nochart">
            No numeric data returned for this chart.
          </div>
        )}
      </div>
    </div>
  );
};




export default StockChatbot;
